import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import ClientChip from "../../../components/molecules/ClientChip.molecule"
import ModalForm from "../../../components/compounds/modalForm/ModalForm.compunds"
import FormContainer from "../../../components/layouts/containers/FormContainer.container"
import FieldContainer from "../../../components/layouts/containers/FieldContainer.container"
import {
  fieldsHaveErrors,
  checkAllFields,
  validateFieldValue,
} from "../../../utils/validations.utils"
import _ from "lodash"
import useToast from "../../../hooks/useToast.hook"
import {
  GENERIC_SAVE_ERROR_MESSAGE,
  GENERIC_SAVE_SUCCESS_MESSAGE,
} from "../../../consts/modules/vacations.consts"
import useReportFiltersContext from "../../../hooks/contextConsumers/useReportFiltersContext.hook"
import { UNIFIEDDATE_VALUES } from "../../../consts/CompoundRangeDatePicker"
import UnifiedDatePickerForm from "../../../components/molecules/UnifiedDatePickerForm.molecule"
import { DateTime } from "luxon"
import {
  disableDatetimePickerHours,
  disableDatetimePickerMinutes,
} from "../../../utils/date.utils"
import Divider from "../../../components/atoms/Divider.atom"
import InputTextForm from "../../../components/molecules/InputTextForm.molecule"
import InputNumberForm from "../../../components/molecules/InputNumberForm.molecule"
import {
  editClientEstimation,
  getSprintClientEstimation,
} from "../../../services/clientReport.service"
import {
  ESTIMATION_MAX_INPUT_NUMBER_VALUE,
  ESTIMATION_MAX_MOTIVE_LENGTH,
  MODAL_FORM_EDIT_CLIENT_ESTIMATION_INITIAL_STATE,
  MODAL_FORM_EDIT_CLIENT_ESTIMATION_VALIDATIONS,
} from "../../../consts/modules/clientReport.consts"

const ModalFormEditClientEstimation = ({
  visible,
  setVisible,
  sprintData,
  onComplete = () => {},
}) => {
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState(
    _.cloneDeep(MODAL_FORM_EDIT_CLIENT_ESTIMATION_INITIAL_STATE)
  )
  const [validations, setValidations] = useState(
    _.cloneDeep(MODAL_FORM_EDIT_CLIENT_ESTIMATION_VALIDATIONS)
  )
  const { setSuccessMessage, setErrorMessage } = useToast()
  const { clientReportClients, clientReportSelectedClient } =
    useReportFiltersContext()
  const client = clientReportClients.find(
    (client) => client.clientId === clientReportSelectedClient
  )

  useEffect(() => {
    if (!sprintData?.id || !visible) return
    fetchSprintClientEstimation()
  }, [visible, sprintData?.id])

  const fetchSprintClientEstimation = async () => {
    setLoading(true)
    try {
      if (!sprintData?.id || !clientReportSelectedClient) {
        resetForm()
        setLoading(false)
        return
      }
      const response = await getSprintClientEstimation(sprintData?.id)
      if (!response?.success) {
        throw new Error(
          response?.message || "Error al obtener la estimación del sprint"
        )
      }
      const estimation = response?.result || {}
      const todayDate = DateTime.now()
      setFormData((prev) => ({
        ...prev,
        date: todayDate,
        TO_DEVELOP: {
          label: "Puntos por desarrollar",
          value: estimation?.TO_DEVELOP?.value || 0,
        },
        TO_ESTIMATE: {
          label: "Puntos por estimar",
          value: estimation?.TO_ESTIMATE?.value || 0,
          userStories: estimation?.TO_ESTIMATE?.userStories || 0,
        },
        TO_DESIGN: {
          label: "Puntos por diseñar",
          value: estimation?.TO_DESIGN?.value || 0,
          userStories: estimation?.TO_DESIGN?.userStories || 0,
        },
        TO_DEFINE: {
          label: "Puntos por definir",
          value: estimation?.TO_DEFINE?.value || 0,
          userStories: estimation?.TO_DEFINE?.userStories || 0,
        },
      }))
      setValidations(
        _.cloneDeep({
          ...validations,
          date: {
            ...validations.date,
            value: todayDate,
          },
        })
      )
    } catch (error) {
      console.error("Error fetching sprint client estimation:", error)
      setVisible(false)
    } finally {
      setLoading(false)
    }
  }
  const handleSubmit = async () => {
    checkAllFields(validations, setValidations)
    if (fieldsHaveErrors(validations)) {
      return
    }
    setLoading(true)
    try {
      const response = await editClientEstimation(
        sprintData?.id,
        clientReportSelectedClient,
        formData.date,
        formData.motive,
        {
          TO_DEVELOP: formData.TO_DEVELOP,
          TO_ESTIMATE: formData.TO_ESTIMATE,
          TO_DESIGN: formData.TO_DESIGN,
          TO_DEFINE: formData.TO_DEFINE,
        }
      )
      if (!response.success) {
        throw new Error(response.message)
      }
      resetForm()
      onComplete()
      setSuccessMessage(GENERIC_SAVE_SUCCESS_MESSAGE)
    } catch (error) {
      setErrorMessage(GENERIC_SAVE_ERROR_MESSAGE)
    } finally {
      setLoading(false)
      setVisible(false)
    }
  }

  const resetForm = () => {
    setFormData(_.cloneDeep(MODAL_FORM_EDIT_CLIENT_ESTIMATION_INITIAL_STATE))
    setValidations(_.cloneDeep(MODAL_FORM_EDIT_CLIENT_ESTIMATION_VALIDATIONS))
  }

  return (
    <ModalForm
      visible={visible}
      setVisible={setVisible}
      header={
        <ModalFormEditClientEstimationHeaderTemplate
          clientCode={client?.code}
          clientName={client?.name}
        />
      }
      className="add-budget-modal"
      saveLabel="Aceptar"
      service={handleSubmit}
      disabledSaveBtn={loading || fieldsHaveErrors(validations)}
      cleanUp={resetForm}
    >
      <FormContainer className="edit-client-basic-data-estimation-form">
        <FieldContainer md={6}>
          <UnifiedDatePickerForm
            label="Fecha del ajuste"
            className="min-w-full"
            hasSingleWeek={false}
            hasSingleMonth={false}
            hasDayPeriod={false}
            hidePeriodSelector
            hideChevron
            dateRange={[formData.date, formData.date]}
            setDateRange={(e) => {
              setFormData((prev) => ({
                ...prev,
                date: e[0],
              }))
              setValidations(validateFieldValue(validations, "date", e[0]))
            }}
            period={UNIFIEDDATE_VALUES.SINGLEDATETIME}
            format="dd/MM/yyyy HH:mm"
            datePickerProps={{
              className: "w-full",
              maxDate: DateTime.now(),
              picker: "datetime",
              showNow: false,
              showTime: {
                format: "HH:mm",
                minuteStep: 15,
                secondStep: 10,
              },
              disabledTime: (current) => {
                return {
                  disabledHours: () =>
                    disableDatetimePickerHours(
                      current,
                      DateTime.fromISO("2000-01-01"),
                      DateTime.now()
                    ),
                  disabledMinutes: () =>
                    disableDatetimePickerMinutes(
                      current,
                      DateTime.fromISO("2000-01-01"),
                      DateTime.now()
                    ),
                }
              },
            }}
            error={validations.date?.errors}
          />
        </FieldContainer>
        <FieldContainer md={6}>
          <InputTextForm
            label="Motivo"
            name="motive"
            value={formData.motive}
            onChange={({ target: { name, value } }) => {
              setFormData((prev) => ({
                ...prev,
                motive: value,
              }))
              setValidations(validateFieldValue(validations, "motive", value))
            }}
            error={validations.motive?.errors}
            maxLength={ESTIMATION_MAX_MOTIVE_LENGTH}
          />
        </FieldContainer>
      </FormContainer>
      <FormContainer className="edit-client-points-estimation-form">
        {["TO_DEVELOP", "TO_ESTIMATE", "TO_DESIGN", "TO_DEFINE"].map(
          (field, index) => (
            <React.Fragment key={field}>
              <FieldContainer
                md={"userStories" in formData[field] ? 6 : 12}
                col={"userStories" in formData[field] ? 6 : 12}
              >
                <InputNumberForm
                  key={`${field}-points-value`}
                  label={formData[field].label}
                  className="mb-3"
                  labelClassName="mb-2"
                  value={formData[field].value || 0}
                  onChange={(e) => {
                    let value = parseInt(e.value, 10)
                    if (value > ESTIMATION_MAX_INPUT_NUMBER_VALUE)
                      value = ESTIMATION_MAX_INPUT_NUMBER_VALUE
                    setFormData((prev) => ({
                      ...prev,
                      [field]: {
                        ...prev[field],
                        value: isNaN(value) ? 0 : value,
                      },
                    }))
                    setValidations(
                      validateFieldValue(validations, field, {
                        value: isNaN(value) ? 0 : value,
                        userStories: formData[field].userStories || 0,
                      })
                    )
                  }}
                  min={0}
                  max={ESTIMATION_MAX_INPUT_NUMBER_VALUE}
                  maxLength={
                    ESTIMATION_MAX_INPUT_NUMBER_VALUE.toString().length + 1
                  }
                  showButtons
                  step={1}
                />
              </FieldContainer>
              {"userStories" in formData[field] && (
                <FieldContainer col={"userStories" in formData[field] ? 6 : 12}>
                  <InputNumberForm
                    label="Historias"
                    className="mb-3"
                    labelClassName="mb-2"
                    value={formData[field].userStories || 0}
                    onChange={(e) => {
                      let value = parseInt(e.value, 10)
                      if (value > ESTIMATION_MAX_INPUT_NUMBER_VALUE)
                        value = ESTIMATION_MAX_INPUT_NUMBER_VALUE
                      setFormData((prev) => ({
                        ...prev,
                        [field]: {
                          ...prev[field],
                          userStories: isNaN(value) ? 0 : value,
                        },
                      }))
                      setValidations(
                        validateFieldValue(validations, field, {
                          value: isNaN(value) ? 0 : value,
                          userStories: formData[field].userStories || 0,
                        })
                      )
                    }}
                    min={0}
                    max={ESTIMATION_MAX_INPUT_NUMBER_VALUE}
                    maxLength={
                      ESTIMATION_MAX_INPUT_NUMBER_VALUE.toString().length + 1
                    }
                    showButtons
                    step={1}
                  />
                </FieldContainer>
              )}
            </React.Fragment>
          )
        )}
      </FormContainer>
    </ModalForm>
  )
}

const ModalFormEditClientEstimationHeaderTemplate = ({
  title = "Editar estimación",
  clientCode = "",
  clientName = "",
}) => {
  if (!clientCode) {
    return null
  }
  return (
    <>
      <div className="flex gap-3 align-items-center">
        <i
          className={`pi pi-pencil text-3xl`}
          style={{ color: "var(--clr-info__dark)" }}
        />
        <div className="flex flex-column gap-2">
          <span className="text-3xl font-semibold font-cairo line-height-1">
            {title}
          </span>
          <div className="flex gap-2 align-items-center">
            <ClientChip clientCode={clientCode} />
            <span className="font-semibold font-cairo line-height-1">
              {clientName}
            </span>
          </div>
        </div>
      </div>
      <Divider className="m-0 mt-3" stroke="sm" />
    </>
  )
}

export default ModalFormEditClientEstimation

ModalFormEditClientEstimation.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  sprintData: PropTypes.object.isRequired,
  onComplete: PropTypes.func,
}
